import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Link} from "react-router";
import {AnchorButton, Button, Checkbox, Menu, MenuItem, Spinner, Tag, Popover} from "@blueprintjs/core";

import {SearchableDataGrid} from "../../../../components/tables/data-grid";
import {organisations} from "../../../../services/api";
import * as AuthAPI from "../../../../services/auth-api";
import {DASHBOARD_APP_BASE, PRERELEASE_DASHBOARD_APP_BASE, AVL_ENVIRONMENT} from "../../../../config/vars";
import {DataPageContext} from "../../context";
import {UserDrawer} from "./user-drawer";
import {InviteUser} from "./invite-user";
import {timeAgo} from "../../../../utils/time";

// -------------------------------------------------
// ----------------------------------- columns model
const getUserModelColumns = (onEditUser) => [
    {
        key: 'id',
        name: 'Id',
        width: 50
    },
    {
        key: 'fullName',
        name: 'Name',
        // width: 200,
        resizable: true,
        sortable: true,
        filterable: true,
        showDefaultFilter: true,
    },
    {
        key: 'email',
        name: 'Email',
        // width: 300,
        resizable: true,
        sortable: true,
        filterable: true,
        showDefaultFilter: true,
        formatter: (params) =>
            <a href={'mailto:' + params.row.email}>
                {params.row.email}
            </a>
    },
    {
        key: 'auth0Id',
        name: 'Auth0 Id',
        // width: 300,
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'status',
        name: 'Status',
        width: 80,
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'type',
        name: 'Type',
        width: 80,
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'primaryOrgName',
        name: 'Active org',
        width: 100,
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) =>
            <Link to={'/orgs/' + params.row.primaryOrgId}>
                {params.row.primaryOrgName}
            </Link>
    },
    {
        key: 'ownerOrgName',
        name: 'Owner org',
        width: 100,
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) =>
            <Link to={'/orgs/' + params.row.ownerOrgId}>
                {params.row.ownerOrgName}
            </Link>
    },
    {
        key: 'usersOrgs',
        name: 'Other orgs',
        width: 100,
        resizable: true,
        formatter: (params) => {
            const value = params.row.usersOrgs;
            if (!value) return <Spinner size={12} />;
            if (typeof value === 'string') return <div>{value}</div>;
            if (value.length > 1) {
                return (
                    <Popover
                        placement="bottom"
                        content={
                            <Menu>
                                {value.map(org => (
                                    <MenuItem
                                        key={org.id}
                                        text={org.name}
                                        href={'/orgs/' + org.id + '/users'}
                                    />
                                ))}
                            </Menu>
                        }
                    >
                        <Button rightIcon="caret-down" text={`${value.length} orgs`} small />
                    </Popover>
                );
            }
            return <div>-</div>;
        }
    },
    {
        key: 'adopted',
        name: 'Adopted',
        width: 75,
        resizable: true,
        sortable: true,
        formatter: (params) => <Checkbox checked={!!params.row?.adopted} />
    },
    {
        key: 'createdAt',
        name: 'Created',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.createdAt}>{timeAgo(params.row.createdAt)}</span>,
    },
    {
        key: 'updatedAt',
        name: 'Updated',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.updatedAt}>{timeAgo(params.row.updatedAt)}</span>,
    },
    {
        key: 'edit',
        name: 'Edit',
        width: 60,
        formatter: (params) =>
            <Tag icon="edit" round interactive onClick={() => onEditUser(params.row)}/>
    },
    {
        key: 'impersonate',
        name: 'Impersonate',
        width: PRERELEASE_DASHBOARD_APP_BASE ? 175 : 110,
        formatter: (params) => {
            const impUrl = (base) => `${base}/?impersonateId=${encodeURIComponent(params.row.auth0Id)}`;
            return <div>
                <AnchorButton
                    size="small" variant="outlined" intent="primary"
                    href={impUrl(DASHBOARD_APP_BASE)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {AVL_ENVIRONMENT === 'prod' ? 'Production': 'UAT'}
                </AnchorButton>
                {PRERELEASE_DASHBOARD_APP_BASE &&
                    <AnchorButton
                        size="small" variant="outlined" intent="success"
                        href={impUrl(PRERELEASE_DASHBOARD_APP_BASE)}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Dash
                    </AnchorButton>
                }
            </div>;
        }
    }
];


const ManyUsers = ({orgId}) => {
    const {setLoading, setNav, setControls, setCount} = useContext(DataPageContext);
    const [data, setData] = useState(null);
    const [orgName, setOrgName] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [inviting, setInviting] = useState(false);
    const [fetchTrigger, setFetchTrigger] = useState(0);

    const toggleInviting = useCallback(() => setInviting(prev => !prev), []);

    const columns = useMemo(() => getUserModelColumns(setSelectedUser), []);

    useEffect(() => {
        console.log("loading users for org", orgId);
        setLoading({status: true, location: "Loading users..."});

        Promise.all([
            organisations.list(),
            AuthAPI.users.list(orgId),
            AuthAPI.users.listAdopted(orgId)
        ]).then(([orgs, usersRes, adoptedRes]) => {
            const getOrgName = (id) => {
                const org = orgs.find(o => o.id === id);
                return org?.name ?? id ?? '-';
            };

            const allUsers = [
                ...(usersRes.data || []).map(u => ({...u, adopted: false})),
                ...(adoptedRes.data || []).map(u => ({...u, adopted: true}))
            ];

            const enriched = allUsers.map(user => ({
                ...user,
                primaryOrgName: getOrgName(user.primaryOrgId),
                ownerOrgName: getOrgName(user.ownerOrgId)
            }));

            const resolvedOrgName = getOrgName(orgId);
            console.log("Loaded " + enriched.length + " users");
            setData(enriched);
            setOrgName(resolvedOrgName);
            setLoading({status: false, location: resolvedOrgName + " users"});
            setNav({
                left: [{
                    name: getOrgName(orgId),
                    path: "/orgs/" + orgId,
                    icon: "office",
                    subpaths: [
                        {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + orgId},
                        {name: "Devices", icon: "desktop", path: '/devs?org=' + orgId},
                        {name: "Cameras", icon: "camera", path: '/recs?org=' + orgId},
                        {name: "Users", icon: "people", path: '/orgs/' + orgId + '/users'},
                    ]
                }],
            });
            setControls([
                {name: "Invite User", onClick: toggleInviting, icon: "new-person"}
            ]);

            // fetch orgs for each user in background
            Promise.all(enriched.map(async user => {
                try {
                    const {data: userOrgs} = await AuthAPI.users.organisationsForUser(user.id, true);
                    return {
                        ...user,
                        usersOrgs: userOrgs.map(o => ({
                            id: o.orgId,
                            name: getOrgName(o.orgId)
                        }))
                    };
                } catch {
                    return {...user, usersOrgs: 'Unknown'};
                }
            })).then(usersWithOrgs => setData(usersWithOrgs));
        }).catch(error => {
            console.error(error);
            setLoading({status: false, location: "Failed to load users..."});
            setData([]);
        });
    }, [fetchTrigger]);

    const refetch = useCallback(() => {
        setSelectedUser(null);
        setFetchTrigger(prev => prev + 1);
    }, []);

    return <>
        <SearchableDataGrid
            columns={columns}
            data={data ?? []}
            defaultSort='fullName'
            onCount={(filtered, total) => setCount({filtered, total})}
        />
        <UserDrawer
            user={selectedUser}
            orgId={orgId}
            orgName={orgName}
            isOpen={!!selectedUser}
            onClose={() => setSelectedUser(null)}
            onUserUpdated={refetch}
            onUserDeleted={refetch}
        />
        <InviteUser
            isOpen={inviting}
            onClose={() => {
                setInviting(false);
                refetch();
            }}
            selectedOrg={{id: orgId, name: orgName}}
        />
    </>;
}

export {ManyUsers};
